import React, { ReactNode, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { notificationsAPI } from '../../services/api';
import {
  LogOut,
  Sun,
  Moon,
  Menu,
  X,
  Pin,
  PinOff,
  ChevronDown,
  Check,
  HelpCircle,
  PersonStanding,
  QrCode,
  Bell,
} from 'lucide-react';
import { IS_STAGING } from '../../config/appConfig';
// Sidebar items + the visibility predicate now live in config/navigation.ts so
// the route guards (components/RequirePerm.tsx) evaluate the SAME metadata.
// 🚨 Do not re-declare role lists here — that is how the URL drifts more
// permissive than the menu (Plan-RBAC-SchemaRoles-2026-08.md §6.1).
import { NAV_ITEMS } from '../../config/navigation';
import usePermissions from '../../hooks/usePermissions';
import ConfirmModal from '../ConfirmModal';
import PlanBanner from '../PlanBanner';
import PendingPlanBanner from '../PendingPlanBanner';
import PendingCancellationBanner from '../PendingCancellationBanner';
// TrialBanner moved to components/billing/ as part of the trial-expiration
// hard-block ship — Plan-TrialExpiration.md §C Phase 2.4. New banner only
// fires at 1-3 days remaining (vs old "every trialing render"); the old
// component file is left in place but no longer mounted.
import TrialBanner from '../billing/TrialBanner';
import TrialExpiredBlocker from '../billing/TrialExpiredBlocker';
import SubscriptionRequiredGate from '../billing/SubscriptionRequiredGate';
import MaintenanceUpcomingBanner from '../maintenance/MaintenanceUpcomingBanner';
import MaintenanceOngoingBlocker from '../maintenance/MaintenanceOngoingBlocker';
import { useTrialStatus } from '../../contexts/TrialStatusContext';
import AccessibilityPanel from '../Accessibility/AccessibilityPanel';
import ProdagiHelpWidget from '../ProdagiHelpWidget';
import NotificationsPopover from '../notifications/NotificationsPopover';
import ChangelogBanner from '../ChangelogBanner';
import LiveAvatarAdModal from '../LiveAvatarAdModal';

// Persistent setup-dock — lazy-imported on BOTH builds (Shopify + app/main).
// The component is build-aware internally (branches its step arrays + copy +
// localStorage namespace on VITE_BILLING_PROVIDER); see
// components/setup/SetupTour.tsx. Replaces the prior react-joyride coach
// marks (e9fd525) with a fixed-positioned dock that never blocks the UI.
const SetupTour = React.lazy(() => import('../setup/SetupTour'));

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { activeWorkspace, workspaces, setActiveWorkspace } = useWorkspace();
  const { canAccessNavItem } = usePermissions();
  const { isExpired: trialExpired, requiresPayment } = useTrialStatus();
  const location = useLocation();

  // Trial-expired hard-block (Plan-TrialExpiration.md §C Phase 2.3).
  // Only `/profile` and `/billing` pass through so the user can pay /
  // see contact info. Sidebar workspace switcher + sign-out button are
  // mounted ABOVE the blocker (sidebar is z-50, blocker is z-40) so
  // multi-workspace users can pivot to a non-expired workspace and the
  // logout escape hatch is always available.
  const TRIAL_ALLOWLIST = React.useMemo(() => ['/profile', '/billing'], []);
  const showTrialBlocker =
    trialExpired && !TRIAL_ALLOWLIST.includes(location.pathname);

  // Plan-RemoveFreeTrial.md — onboarding gate for a brand-new, never-trialed
  // workspace (`subscription_status === "incomplete"`). Same allow-list as the
  // trial blocker so the owner can reach /billing to pick a plan and /profile.
  // Mutually exclusive with the trial blocker in practice (a workspace is
  // either "incomplete" or "trialing", never both); guard against the trial
  // blocker anyway so only one full-screen overlay ever renders.
  const showSubscriptionGate =
    requiresPayment &&
    !showTrialBlocker &&
    !TRIAL_ALLOWLIST.includes(location.pathname);

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = React.useState(false);
  const workspaceMenuRef = React.useRef<HTMLDivElement>(null);
  // Header accessibility popover — quick-access dropdown rendering
  // <AccessibilityPanel /> inline so users can tweak preferences without
  // navigating away. Mirrors the workspace-switcher outside-click pattern.
  const [a11yMenuOpen, setA11yMenuOpen] = React.useState(false);
  const a11yMenuRef = React.useRef<HTMLDivElement>(null);
  // Header notifications popover — Plan-NotificationsHeaderPopover.md.
  // Outside-click + Esc dismissal is owned by the popover itself; we just
  // toggle visibility here. The trigger ref is forwarded to the popover so
  // a second tap on the Bell isn't treated as "outside" — without that, the
  // popover closes via outside-click and the button's `setOpen(o => !o)`
  // immediately flips it back open.
  const [notifMenuOpen, setNotifMenuOpen] = React.useState(false);
  const notifTriggerRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!workspaceMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (workspaceMenuRef.current && !workspaceMenuRef.current.contains(e.target as Node)) {
        setWorkspaceMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [workspaceMenuOpen]);

  React.useEffect(() => {
    if (!a11yMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (a11yMenuRef.current && !a11yMenuRef.current.contains(e.target as Node)) {
        setA11yMenuOpen(false);
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setA11yMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [a11yMenuOpen]);

  // Sidebar hover and pin state
  const [isHovered, setIsHovered] = React.useState(false);
  const [isPinned, setIsPinned] = React.useState(false);

  const isExpanded = isHovered || isPinned;

  React.useEffect(() => {
    let eventSource: EventSource | null = null;
    let isMounted = true;
    let reconnectTimeout: NodeJS.Timeout;

    const setupSSE = () => {
      if (!isMounted) return;
      const token = localStorage.getItem('token');
      if (!token) return;

      const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const workspaceId = localStorage.getItem('active_workspace_id');
      const streamUrl = new URL(`${backendUrl}/api/notifications/stream`);
      streamUrl.searchParams.set('token', token);
      if (workspaceId) streamUrl.searchParams.set('workspace_id', workspaceId);
      eventSource = new EventSource(streamUrl.toString());

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.count !== undefined) {
            setUnreadCount(data.count);
          }
          if (data.type) {
            window.dispatchEvent(new CustomEvent('app:notification', { detail: data }));
          }
        } catch (err) {
          console.error('Error parsing SSE data', err);
        }
      };

      // Typed SSE event piggyback: backend emits `event: maintenance_state`
      // (separate from the default `message` events above) when the
      // operator-controlled maintenance window changes state. Re-broadcast
      // as a CustomEvent so MaintenanceContext can update without owning
      // its own EventSource. Plan-MaintenanceBanners.md §C.3 + §E.1.
      eventSource.addEventListener('maintenance_state', (event) => {
        try {
          const data = JSON.parse((event as MessageEvent).data);
          window.dispatchEvent(
            new CustomEvent('app:maintenance_state', { detail: data }),
          );
        } catch (err) {
          console.error('Error parsing maintenance_state SSE data', err);
        }
      });

      eventSource.onerror = (error) => {
        console.error('SSE Error', error);
        eventSource?.close();
        if (isMounted) {
          reconnectTimeout = setTimeout(setupSSE, 5000);
        }
      };
    };

    if (user) {
      // Fetch initial count
      const fetchUnread = async () => {
        try {
          const res = await notificationsAPI.getUnreadCount();
          if (isMounted) setUnreadCount(res.data.count);
        } catch (err) {
          // ignore
        }
      };
      fetchUnread();

      setupSSE();
    }

    return () => {
      isMounted = false;
      clearTimeout(reconnectTimeout);
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [user, activeWorkspace]);

  // Sidebar = the shared nav metadata filtered by the shared predicate, with
  // the live unread badge attached. `canAccessNavItem` reproduces the previous
  // inline `hasRole` exactly while the `rbac_route_guards` flag is off.
  //
  // 🚨 The list can no longer come out empty: Settings carries `alwaysVisible`,
  // so every role — including a custom role matching zero items — always has a
  // landing spot (Plan §9.1 / §11.4). The Profile card below the nav is the
  // other guaranteed destination.
  const navigation = NAV_ITEMS.filter(canAccessNavItem).map(item =>
    item.href === '/inbox' ? { ...item, badge: unreadCount } : item,
  ) as (typeof NAV_ITEMS[number] & { badge?: number })[];


  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 flex flex-col">
      {/* Sidebar */}
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`fixed inset-y-0 left-0 z-50 glass backdrop-blur-xl shadow-2xl transform transition-all duration-300 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${isExpanded ? 'w-64' : 'w-64 lg:w-20'}`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Logo */}
          <div className={`flex items-center p-6 border-b border-white/10 justify-between ${!isExpanded ? 'lg:justify-center' : ''}`}>
            <Link to="/" className="flex flex-col items-start">
              {/* Full Logo (Visible on mobile, or on desktop when expanded) */}
              <img
                src={theme === 'light' ? "/logo-purple.png" : "/logo-white.png"}
                alt="AskProdigy.ai"
                className={`h-12 w-auto object-contain ${!isExpanded ? 'lg:hidden' : ''}`}
              />
              {/* Icon Only Logo (Visible only on desktop when collapsed) */}
              <img
                src={theme === 'light' ? "/logo-only-purple.png" : "/logo-only-white.png"}
                alt="AskProdigy.ai"
                className={`h-8 w-auto object-contain hidden ${!isExpanded ? 'lg:block' : ''}`}
              />
              {/* Staging environment badge — gated on VITE_APP_ENV=staging at
                  build time. Renders amber subtitle in expanded sidebar +
                  smaller variant when sidebar is collapsed so the env is
                  always visible at a glance. Tree-shakes to null on prod. */}
              {IS_STAGING && (
                <>
                  <span
                    className={`mt-1 text-[10px] uppercase tracking-widest font-bold text-amber-600 dark:text-amber-400 ${!isExpanded ? 'lg:hidden' : ''}`}
                    aria-label="Staging environment"
                  >
                    Staging
                  </span>
                  <span
                    className={`mt-0.5 text-[8px] uppercase tracking-wider font-bold text-amber-600 dark:text-amber-400 hidden ${!isExpanded ? 'lg:block' : ''}`}
                    aria-hidden="true"
                  >
                    Staging
                  </span>
                </>
              )}
            </Link>
            
            {/* Mobile Close Button */}
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-gray-600 dark:text-gray-400">
              <X className="w-6 h-6" />
            </button>

            {/* Pin/Unpin Button (Desktop Only) */}
            {isExpanded && (
              <button 
                onClick={() => setIsPinned(!isPinned)} 
                className="hidden lg:block text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                title={isPinned ? "Unpin sidebar" : "Pin sidebar"}
              >
                {isPinned ? <PinOff className="w-5 h-5" /> : <Pin className="w-5 h-5" />}
              </button>
            )}
          </div>

          {/* Workspace Switcher (only when 2+ workspaces) */}
          {workspaces.length > 1 && activeWorkspace && (
            <div className="px-4 pt-4 pb-2" ref={workspaceMenuRef}>
              <button
                onClick={() => setWorkspaceMenuOpen((o) => !o)}
                className={`w-full flex items-center gap-2 rounded-xl glass border border-white/20 hover:border-primary/40 transition-all px-3 py-2.5 ${
                  !isExpanded ? 'lg:justify-center lg:px-2' : ''
                }`}
                title={!isExpanded ? activeWorkspace.name : undefined}
              >
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {activeWorkspace.name.charAt(0).toUpperCase()}
                </div>
                <div className={`flex-1 min-w-0 text-left ${!isExpanded ? 'lg:hidden' : ''}`}>
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 leading-none">
                    Workspace
                  </p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight mt-0.5">
                    {activeWorkspace.name}
                  </p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0 transition-transform ${
                    workspaceMenuOpen ? 'rotate-180' : ''
                  } ${!isExpanded ? 'lg:hidden' : ''}`}
                />
              </button>

              {workspaceMenuOpen && (
                <div
                  className={`mt-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden max-h-72 overflow-y-auto ${
                    !isExpanded ? 'lg:absolute lg:left-20 lg:top-[100px] lg:w-56 lg:z-50' : ''
                  }`}
                >
                  {workspaces.map((ws) => {
                    const isActive = ws.id === activeWorkspace.id;
                    return (
                      <button
                        key={ws.id}
                        onClick={() => {
                          setActiveWorkspace(ws);
                          setWorkspaceMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors ${
                          isActive
                            ? 'bg-primary/10 dark:bg-primary/20'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <div className="w-6 h-6 rounded bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                          {ws.name.charAt(0).toUpperCase()}
                        </div>
                        <span
                          className={`flex-1 truncate text-sm ${
                            isActive
                              ? 'font-semibold text-primary dark:text-primary'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {ws.name}
                        </span>
                        {isActive && <Check className="w-4 h-4 text-primary shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 min-h-0 p-4 space-y-2 overflow-y-auto overflow-x-hidden">
            {navigation.map((item) => {
              const Icon = item.icon;
              // Exact match by default (every other item is a flat route).
              // Items that own sub-routes opt in to prefix matching so the tab
              // stays highlighted on e.g. /orchestrator/chat.
              const isActive = item.prefix
                ? location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
                : location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-tour-target={item.tourTarget}
                  className={`relative flex items-center px-4 py-3 rounded-xl transition-all duration-300 ${
                    isActive
                      ? 'bg-gradient-to-r from-primary to-purple-600 text-white shadow-lg scale-105'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-800/50 hover:scale-105'
                  } space-x-3 ${!isExpanded ? 'lg:space-x-0 lg:justify-center' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  title={!isExpanded ? item.name : undefined}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span className={`font-medium flex-1 truncate ${!isExpanded ? 'lg:hidden' : ''}`}>
                    {item.name}
                  </span>
                  
                  {/* Badge Full */}
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className={`bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full ${!isExpanded ? 'lg:hidden' : ''}`}>
                      {item.badge}
                    </span>
                  )}

                  {/* Badge Dot for collapsed state */}
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className={`absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full hidden ${!isExpanded ? 'lg:block' : ''}`}></span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section.
              `index.html` sets `viewport-fit=cover`, and this sidebar is
              `fixed inset-y-0` — so on iOS the Logout button (the last flex
              child, flush to the bottom edge) sits under the home indicator
              and Safari's bottom toolbar, and can't be tapped. Pad past the
              inset. `env(safe-area-inset-bottom)` resolves to 0px on desktop
              and Android, so this is a no-op everywhere else. */}
          <div className="p-4 pb-[calc(1rem_+_env(safe-area-inset-bottom))] border-t border-white/10">
            <Link
              to="/profile"
              onClick={() => setSidebarOpen(false)}
              className={`block glass rounded-xl mb-3 transition-all duration-300 hover:scale-105 hover:border-primary/30 p-4 ${!isExpanded ? 'lg:p-2 lg:flex lg:justify-center' : ''}`}
              title={!isExpanded ? 'Profile' : undefined}
            >
              <div className={`flex items-center space-x-3 mb-1 ${!isExpanded ? 'lg:space-x-0 lg:mb-0' : ''}`}>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-semibold shadow-lg shrink-0">
                  {user?.full_name?.charAt(0).toUpperCase()}
                </div>
                <div className={`flex-1 min-w-0 ${!isExpanded ? 'lg:hidden' : ''}`}>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {user?.full_name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{user?.email}</p>
                </div>
              </div>
            </Link>
            <button
              onClick={() => setShowLogoutModal(true)}
              className={`w-full flex items-center justify-center space-x-2 px-4 py-3 transition-all duration-300 font-medium rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 ${!isExpanded ? 'lg:px-0 lg:space-x-0' : ''}`}
              title={!isExpanded ? 'Logout' : undefined}
            >
              <LogOut className="w-5 h-5 shrink-0" />
              <span className={!isExpanded ? 'lg:hidden' : ''}>Logout</span>
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={logout}
        title="Confirm Logout"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        isDangerous={true}
      />

      {/* Main content */}
      <div className={`transition-all duration-300 ${isPinned ? 'lg:pl-64' : 'lg:pl-20'} flex flex-col flex-1 min-w-0 w-full`}>
        {/* Top bar */}
        <div className="sticky top-0 z-40 glass backdrop-blur-xl border-b border-white/10 shadow-lg">
          <div className="flex items-center justify-between px-6 py-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-700 dark:text-gray-300">
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1"></div>
            <div className="flex items-center gap-2">
              <a
                href={(() => {
                  // Backend route is `/admin-help-center/` (with slash).
                  // Linking to the no-slash variant triggers a FastAPI
                  // `redirect_slashes=True` 307 round-trip, so normalize
                  // to a trailing slash regardless of how the env var or
                  // the dev fallback is configured.
                  const raw = import.meta.env.VITE_PRODAGI_HC_URL
                    || `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/admin-help-center`;
                  return raw.endsWith('/') ? raw : `${raw}/`;
                })()}
                target="_blank"
                rel="noopener noreferrer"
                title="AskProdigy AI Help Center"
                aria-label="AskProdigy AI Help Center"
                className="p-3 rounded-xl glass hover:scale-110 transition-all duration-300 text-gray-700 dark:text-gray-300 inline-flex"
              >
                <HelpCircle className="w-5 h-5" />
              </a>
              {/* Prodagi quick-links QR-codes page. Opens in a new tab so
                  it's easy to share the URL itself (sales reps, kiosks). */}
              <a
                href="/qr-codes"
                target="_blank"
                rel="noopener noreferrer"
                title="AskProdigy links"
                aria-label="AskProdigy quick links (QR codes)"
                className="p-3 rounded-xl glass hover:scale-110 transition-all duration-300 text-gray-700 dark:text-gray-300 inline-flex"
              >
                <QrCode className="w-5 h-5" />
              </a>
              {/* Notifications popover — Plan-NotificationsHeaderPopover.md.
                  Bell button with red unread badge; clicking opens the
                  inline popover (NotificationsPopover handles its own
                  outside-click/Esc dismissal). The `unreadCount` state
                  here is kept in sync via the existing SSE stream above —
                  the popover also bubbles up changes from
                  mark-as-read / mark-all-as-read so the badge stays live
                  without an extra round-trip. */}
              <div className="relative">
                <button
                  ref={notifTriggerRef}
                  onClick={() => setNotifMenuOpen((o) => !o)}
                  aria-haspopup="dialog"
                  aria-expanded={notifMenuOpen}
                  aria-label="Notifications"
                  title="Notifications"
                  className={`relative p-3 rounded-xl glass hover:scale-110 transition-all duration-300 text-gray-700 dark:text-gray-300 ${
                    notifMenuOpen ? 'ring-2 ring-primary/40' : ''
                  }`}
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
                      aria-label={`${unreadCount} unread notifications`}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                {notifMenuOpen && (
                  <NotificationsPopover
                    onClose={() => setNotifMenuOpen(false)}
                    onCountChange={(c) => setUnreadCount(c)}
                    triggerRef={notifTriggerRef}
                  />
                )}
              </div>
              {/* Accessibility quick-access popover.
                  Renders <AccessibilityPanel /> inline so users get same
                  controls as /settings without leaving the current page.
                  Closes on outside click or Esc (see effect above). */}
              <div className="relative" ref={a11yMenuRef}>
                <button
                  onClick={() => setA11yMenuOpen(o => !o)}
                  aria-haspopup="dialog"
                  aria-expanded={a11yMenuOpen}
                  aria-label="Accessibility settings"
                  title="Accessibility settings"
                  className={`p-3 rounded-xl glass hover:scale-110 transition-all duration-300 text-gray-700 dark:text-gray-300 ${
                    a11yMenuOpen ? 'ring-2 ring-primary/40' : ''
                  }`}
                >
                  <PersonStanding className="w-5 h-5" />
                </button>
                {a11yMenuOpen && (
                  <div
                    role="dialog"
                    aria-label="Accessibility settings"
                    className="fixed left-2 right-2 top-20 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-w-[calc(100vw-1rem)] z-50 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl p-5 max-h-[80vh] overflow-y-auto"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <PersonStanding className="w-5 h-5 text-primary" />
                      <h3 className="text-sm font-bold text-gray-900 dark:text-white">Accessibility</h3>
                    </div>
                    <AccessibilityPanel />
                  </div>
                )}
              </div>
              <button
                onClick={toggleTheme}
                className="p-3 rounded-xl glass hover:scale-110 transition-all duration-300 text-gray-700 dark:text-gray-300"
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Plan Banners */}
        {/* Changelog banner — single admin-promoted entry; no-op if none
            active or user has dismissed it. Plan-ChangelogBanner.md §Phase 2a. */}
        <ChangelogBanner />
        <PendingPlanBanner />
        <PendingCancellationBanner />
        <TrialBanner />
        <PlanBanner />
        {/*
          Maintenance upcoming banner — soft amber strip rendered only
          when MaintenanceContext.isUpcoming. No-op (returns null) on
          mode=off and mode=ongoing, safe to mount unconditionally.
          Plan-MaintenanceBanners.md §E.1.
        */}
        <MaintenanceUpcomingBanner />

        {/* Page content
            Remount the current page subtree on workspace switch so every
            page's initial-mount fetches re-fire against the new workspace.
            Profile is user-scoped (not workspace-scoped) and holds unsaved
            form input, so it keeps a stable key to preserve that state. */}
        <main
          key={
            location.pathname === '/profile'
              ? 'profile-stable'
              : `ws-${activeWorkspace?.id ?? 'none'}`
          }
          className="p-6"
        >
          {children}
        </main>

        {/*
          Trial-expired blocker. Rendered INSIDE the main-content panel
          (`fixed inset-0` + parent wrapper's `lg:pl-20`/`lg:pl-64`
          translates the panel away from the sidebar; blocker uses z-40
          so the z-50 sidebar stays clickable, even on mobile when the
          drawer is open). Underlying route stays mounted so paying →
          state flip un-mounts the blocker without a remount cascade.
          Plan-TrialExpiration.md §C Phase 2.3.
        */}
        {showTrialBlocker && <TrialExpiredBlocker />}

        {/*
          Subscription-required onboarding gate (Plan-RemoveFreeTrial.md).
          Same full-screen placement / z-40 / allow-list as the trial
          blocker, but onboarding-framed copy for brand-new workspaces that
          never received a trial. Mutually exclusive with the trial blocker.
        */}
        {showSubscriptionGate && <SubscriptionRequiredGate />}

        {/*
          Maintenance ongoing blocker — full-viewport modal (z-40,
          sidebar at z-50 stays clickable) rendered only when
          MaintenanceContext.isOngoing AND user is NOT on an
          allowlisted route (/profile, /billing, etc). No-op (returns
          null) on mode=off and mode=upcoming, safe to mount
          unconditionally. Plan-MaintenanceBanners.md §E.1.
        */}
        <MaintenanceOngoingBlocker />
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Plan-ProdagiHelpWidget-CustomerDashboard.md Phase 2 — floating
          help launcher powered by the admin Prodagi singleton agent.
          No-op when VITE_PRODAGI_GUIDE_AGENT_ID is unset or the user is
          unauthenticated. Mounted at the layout level (not per page) so
          the launcher persists across navigation. */}
      <ProdagiHelpWidget />

      {/* Post-login Live Avatar promo — small, non-blocking, once-per-
          session modal for ALL logged-in users. Self-gates to null when
          VITE_LIVE_AVATAR_ARTICLE_URL is unset or the session flag is
          set (Plan-LiveAvatarSetupStep-LoginPopup.md Task 2). */}
      {user && <LiveAvatarAdModal />}

      {/* Setup Tour dock — Linear/Intercom-style persistent checklist
          widget, mounted on BOTH builds (build-aware internally). Always
          visible (when not dismissed/completed), never blocks the UI. Owns
          its own workspace-scoped localStorage state + auto-show decision;
          Dashboard.tsx still dispatches the 'prodagi:open-setup-tour'
          CustomEvent for the in-header Setup Guide button and the `?setup=1`
          URL force-open trigger. Mounted at app shell so it persists across
          routes (Linear pattern). */}
      {SetupTour && user && (
        <Suspense fallback={null}>
          <SetupTour />
        </Suspense>
      )}
    </div>
  );
};

export default Layout;
